/** @jsxImportSource @emotion/react */
import React, { useState } from "react";
import { Interpolation } from "@emotion/react";
import { Theme } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlusCircle } from "@fortawesome/free-solid-svg-icons";

import Colors, { shadeColor } from "styles/colors";
import { useSurfaceRipple } from "components/hooks/useSurfaceRipple";
import AddTagDialog from "pages/reports/bi-dashboard-next/components/organisms/Reports.AddTagDialog.organism";
import { useReportTags } from "pages/reports/bi-dashboard-next/hooks/useReportTags";

interface AddTagButtonProps {
  onClick?: () => void;
  css?: Interpolation<Theme>;
  size?: "small";
  reportId: number;
}

const AddTagButton: React.FC<AddTagButtonProps> = ({
  onClick,
  css,
  size,
  reportId,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { rippleContainerRef, createRipple, rippleStyles } =
    useSurfaceRipple<HTMLButtonElement>();
  const { t } = useTranslation("reports");
  const { canManageTags } = useReportTags();

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    createRipple(e);
    setIsDialogOpen(true);
    if (onClick) {
      onClick();
    }
  };

  if (!canManageTags) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        ref={rippleContainerRef}
        onClick={handleClick}
        data-qa="add-tag-button"
        css={[
          {
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: 1,
            alignSelf: "center",
            fontSize: size === "small" ? 16 : 22,
            color: Colors.biDashboardView.TAG_DELETE_ICON_BACKGROUND,
            transition: "color 0.2s, background 0.2s",
            outline: "none",
            border: 0,
            background: "none",
            padding: 0,
            margin: 0,
            width: size === "small" ? 16 : undefined,
            height: size === "small" ? 16 : undefined,
            ":hover": {
              color: shadeColor(
                Colors.biDashboardView.TAG_DELETE_ICON_BACKGROUND,
                -0.1,
              ),
            },
            ":focus": {
              outline: "none",
              boxShadow: "none",
            },
          },
          css,
        ]}
        aria-label={t("Add New Tag")}
      >
        <FontAwesomeIcon
          icon={faPlusCircle}
          style={{ fontSize: size === "small" ? 16 : 22 }}
        />
        <span css={rippleStyles} />
      </button>

      <AddTagDialog
        reportId={reportId}
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
      />
    </>
  );
};

export default AddTagButton;
